/**
 * @fileoverview The version an Edit Client form was opened with.
 * @description A refetch of the client's details that returns a new ETag (window focus, someone
 * else's edit) must never reset what was typed, so the form keeps the first version it loaded. Only
 * the person's Refresh adopts the latest one, once its reload has finished. Adjusting state while
 * rendering, not in an effect, keeps the form from flashing the old values.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'

import type { VersionedClientDetails } from '@/features/sales/types/sales-client.types'
import type { UseQueryResult } from '@tanstack/react-query'

/**
 * @description Holds the version the form was opened with.
 * @param {UseQueryResult<VersionedClientDetails>} details - The client's details query.
 * @returns {object} The version the form uses, whether a newer one exists, whether the reload is
 * running, and how to adopt the latest.
 */
export function useClientFormBaseline(details: UseQueryResult<VersionedClientDetails>) {
  const [loaded, setLoaded] = useState<VersionedClientDetails | null>(null)
  const [reloading, setReloading] = useState(false)
  const latest = details.isSuccess ? details.data : null

  if (!loaded && latest) setLoaded(latest)

  /**
   * @description Fetches the latest version and makes the form use it.
   */
  const reload = async () => {
    setReloading(true)
    const result = await details.refetch()
    if (result.data) setLoaded(result.data)
    setReloading(false)
  }

  return {
    loaded,
    reloading,
    changedSince: loaded !== null && latest !== null && latest.etag !== loaded.etag,
    reload,
  }
}
